import { useEffect, useRef, useState } from "react";

function getMousePositionPx(e: React.MouseEvent | MouseEvent) {
  return { x: e.clientX, y: e.clientY };
}

function getTouchPositionPx(e: React.TouchEvent | TouchEvent) {
  const t =
    (e as TouchEvent).touches?.[0] || (e as TouchEvent).changedTouches?.[0];
  return t ? { x: t.clientX, y: t.clientY } : { x: 0, y: 0 };
}

const WHEEL_OPTIONS: AddEventListenerOptions = { passive: false };

export function useMonitoringTooltip() {
  const [tooltip, setTooltip] = useState<any>(null);
  const [scrollHover, setScrollHover] = useState(false);
  const tooltipRootRef = useRef<HTMLDivElement>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const tooltipScrollRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef(tooltip);

  useEffect(() => {
    tooltipRef.current = tooltip;
  }, [tooltip]);

  const showTooltip = (e: any, payload: any) => {
    lastTriggerRef.current = (e.currentTarget ||
      e.target ||
      null) as HTMLElement;
    const { x, y } = (e as any).touches
      ? getTouchPositionPx(e)
      : getMousePositionPx(e);
    const newTooltip = { visible: true, x, y, ...payload };
    setTooltip(newTooltip);
    tooltipRef.current = newTooltip;
  };

  const moveTooltip = (e: any) => {
    const { x, y } = (e as any).touches
      ? getTouchPositionPx(e)
      : getMousePositionPx(e);
    setTooltip((prev: any) => {
      if (!prev?.visible) return prev;
      const next = { ...prev, x, y };
      tooltipRef.current = next;
      return next;
    });
  };

  const hideTooltip = () => {
    setScrollHover(false);
    setTooltip(null);
    tooltipRef.current = null;
  };

  useEffect(() => {
    if (!tooltip?.visible) return;
    const onPointerDown = (e: PointerEvent) => {
      const tipEl = tooltipRootRef.current;
      const triggerEl = lastTriggerRef.current;
      const clickedInsideTooltip = tipEl && tipEl.contains(e.target as Node);
      const clickedOnTrigger =
        triggerEl && triggerEl.contains?.(e.target as Node);
      if (clickedInsideTooltip || clickedOnTrigger) return;
      hideTooltip();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [tooltip?.visible]);

  useEffect(() => {
    if (!scrollHover) return;
    const onWheel = (e: WheelEvent) => {
      const currentTooltip = tooltipRef.current;
      if (!currentTooltip?.visible || currentTooltip?.kind !== "dns") return;
      e.preventDefault();
      const el = tooltipScrollRef.current;
      if (el) el.scrollTop += e.deltaY;
    };
    window.addEventListener("wheel", onWheel, WHEEL_OPTIONS);
    return () => window.removeEventListener("wheel", onWheel, WHEEL_OPTIONS);
  }, [scrollHover]);

  return {
    tooltip,
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipRootRef,
    tooltipScrollRef,
    scrollHover,
    setScrollHover,
  };
}
